'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Mail, MessageSquare, Users } from 'lucide-react';
import type { MockUser } from '@/types';
import { useLanguage } from '@/context/LanguageContext';
import { getUserProfileHref } from '@/utils';
import { BulkEmailModal, DirectMessageModal, Toast } from '@/components/ui';
import { useBulkEmail } from '@/hooks/useBulkEmail';
import { useDirectMessage } from '@/hooks/useDirectMessage';

interface EventAttendeesTabProps {
  attendees: MockUser[];
  attendeeCount: number;
  canSendBulkEmail?: boolean;
  canDirectMessage?: boolean;
  currentUserId?: number;
  eventId?: number;
}

export function EventAttendeesTab({
  attendees,
  attendeeCount,
  canSendBulkEmail = false,
  canDirectMessage = false,
  currentUserId,
  eventId,
}: EventAttendeesTabProps) {
  const { t } = useLanguage();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Map MockUser[] into the shape expected by useBulkEmail
  const approvedMembers = useMemo(
    () => attendees.map((u) => ({ user: { id: u.id } })),
    [attendees]
  );

  const showTimedToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const bulkEmail = useBulkEmail({
    mode: 'event',
    eventId,
    approvedMembers,
    messages: {
      validationError: t.groupTabs.bulkEmailValidationError,
      sent: t.groupTabs.bulkEmailSent,
      sendFailed: t.groupTabs.bulkEmailSendFailed,
      forbidden: t.groupTabs.bulkEmailForbidden,
    },
    onToast: showTimedToast,
  });

  const directMessage = useDirectMessage({
    messages: {
      validationError: t.userDetail.directMessage.validationError,
      sent: t.userDetail.directMessage.sent,
      sendFailed: t.userDetail.directMessage.sendFailed,
      forbidden: t.userDetail.directMessage.forbidden,
      recipientNotFound: t.userDetail.directMessage.recipientNotFound,
    },
    onToast: showTimedToast,
  });

  const showActionColumn = canDirectMessage || canSendBulkEmail;

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">{t.eventDetail.attendees} ({attendeeCount})</h2>
      </div>

      <div className="space-y-3">

      {canSendBulkEmail && bulkEmail.selectedMemberIds.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-background-secondary rounded-lg">
          <span className="text-sm text-foreground-muted">
            {bulkEmail.selectedMemberIds.length} {t.groupTabs.selectedItems}
          </span>
          <button
            type="button"
            onClick={() => bulkEmail.setShowBulkEmailModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-background rounded-md hover:opacity-90"
          >
            <Mail size={14} />
            {t.groupTabs.sendEmail}
          </button>
        </div>
      )}

      {attendees.length === 0 ? (
        <div className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
          <Users size={32} className="mx-auto mb-2 opacity-50" />
          <p>{t.eventDetail.noAttendeesYet}</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[480px]">
            <thead className="bg-primary border-b border-border">
              <tr>
                {canSendBulkEmail && (
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={attendees.length > 0 && bulkEmail.selectedMemberIds.length === attendees.length}
                      onChange={bulkEmail.toggleSelectAllMembers}
                      aria-label={t.groupTabs.selectAllMembers}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                  </th>
                )}
                <th className="text-left text-xs font-semibold text-white px-4 py-3">Name</th>
                <th className="text-left text-xs font-semibold text-white px-4 py-3">Location</th>
                {showActionColumn && (
                  <th className="text-right text-xs font-semibold text-white px-4 py-3">{t.userManagement.table.action}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {attendees.map((user) => (
                <tr key={user.id} className="border-t border-border">
                  {canSendBulkEmail && (
                    <td className="px-4 py-3 align-middle">
                      <input
                        type="checkbox"
                        checked={bulkEmail.selectedMemberIds.includes(user.id)}
                        onChange={() => bulkEmail.toggleMemberSelection(user.id)}
                        aria-label={t.groupTabs.selectMember.replace('{name}', user.displayName)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Link href={getUserProfileHref(user)} className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity">
                      <div
                        className="w-9 h-9 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-xs font-medium text-white"
                        style={{ backgroundColor: user.avatarUrl ? undefined : user.avatarColor }}
                      >
                        {user.avatarUrl ? (
                          <Image src={user.avatarUrl} alt={user.displayName} width={36} height={36} className="w-full h-full object-contain" unoptimized />
                        ) : (
                          user.initials
                        )}
                      </div>
                      <p className="font-medium text-foreground truncate">{user.displayName}</p>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-muted">
                    {user.location ?? <span className="text-foreground-muted/50">—</span>}
                  </td>
                  {showActionColumn && (
                    <td className="px-4 py-3 text-right">
                      {canDirectMessage && user.id !== currentUserId ? (
                        <button
                          type="button"
                          onClick={() => directMessage.open(user.id, user.displayName)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md text-foreground hover:bg-background-secondary transition"
                        >
                          <MessageSquare size={14} />
                          {t.userDetail.messageMe}
                        </button>
                      ) : (
                        <span className="text-sm text-foreground-muted">-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      <BulkEmailModal
        isOpen={bulkEmail.showBulkEmailModal}
        onClose={bulkEmail.closeBulkEmailModal}
        selectedCount={bulkEmail.selectedMemberIds.length}
        subject={bulkEmail.bulkEmailSubject}
        onSubjectChange={bulkEmail.setBulkEmailSubject}
        message={bulkEmail.bulkEmailMessage}
        onMessageChange={bulkEmail.setBulkEmailMessage}
        sending={bulkEmail.sendingBulkEmail}
        onSend={bulkEmail.handleSendBulkEmail}
      />

      <DirectMessageModal
        isOpen={directMessage.isOpen}
        onClose={directMessage.close}
        subject={directMessage.subject}
        onSubjectChange={directMessage.setSubject}
        body={directMessage.body}
        onBodyChange={directMessage.setBody}
        sending={directMessage.sending}
        onSend={directMessage.send}
        labels={{
          title: t.userDetail.directMessage.modalTitle,
          subtitle: t.userDetail.directMessage.modalSubtitle.replace('{name}', directMessage.recipientName),
          subjectLabel: t.userDetail.directMessage.subject,
          subjectPlaceholder: t.userDetail.directMessage.subjectPlaceholder,
          bodyLabel: t.userDetail.directMessage.message,
          bodyPlaceholder: t.userDetail.directMessage.messagePlaceholder,
          cancel: t.userDetail.directMessage.cancel,
          send: t.userDetail.directMessage.send,
          sending: t.userDetail.directMessage.sending,
        }}
      />
    </div>
  );
}
